// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import {
  Button,
  Flex,
  FormItem,
  Modal,
  Form,
  Select,
  UseShowHide,
  useForm,
  Typography,
} from '@teamnine/pcj-ui-v2'
import { useNavigate } from 'react-router-dom'
import { useQueryClient, useQuery } from 'react-query'
import { Search, Add } from '@/utils/material-icons'
import { useTheme } from 'styled-components'
import * as Styled from './Styles'
import * as uuid from 'uuid'

import {
  CustomerStatus,
  Customer as ICustomer,
  NextgenCustomer,
  createCustomer as createCustomerRequest,
  getNextgenCustomers,
} from '@/services/customers'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import { isStgOrPrd } from '@/utils/environment'
import {
  generateUniqueNamePair,
  makeCompoundValue,
  normalizeName,
  parseCompoundValue,
  PRACTICE_REGEX,
} from '@/utils/helpers'
import {
  EXISTING_CUSTOMER_TAG_LABEL,
  isExistingNextgenCustomer,
  UNSOURCED_CUSTOMER_MESSAGE,
} from '@/app/CustomerManagement/utils/nextgen-customers'

type CreateCustomerModalProps = {
  modal: UseShowHide
  customers: ICustomer[]
}

const FORM_NAME = 'new-customer'
const MIN_SEARCH_LENGTH = 2
const DEBOUNCE_MS = 300

export default function CreateCustomerModal({
  modal,
  customers,
}: CreateCustomerModalProps) {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const queryClient = useQueryClient()
  const [form] = useForm()
  const { colors } = useTheme()
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()

  const [isLoading, setIsLoading] = useState(false)
  const [nextgenCustomers, setNextgenCustomers] = useState<NextgenCustomer[]>(
    []
  )
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchMode, setSearchMode] = useState<'gcn' | 'name'>('name')
  const selectedNextgenCustomerRef = useRef<NextgenCustomer | null>(null)

  const placeholderText =
    searchMode === 'gcn'
      ? 'Search by GCN (e.g. -666705)'
      : 'Search by customer name'

  const { existingGcns, existingNormalizedNames, existingShortNames } =
    useMemo(() => {
      const gcns = new Set<string>()
      const names = new Set<string>()
      const shortNames = new Set<string>()

      customers.forEach((customer) => {
        if (customer.accountGcn)
          gcns.add(String(customer.accountGcn).toLowerCase())
        if (customer.name) names.add(normalizeName(String(customer.name)))
        if (customer.shortName)
          shortNames.add(String(customer.shortName).toLowerCase())
        else if (customer.name)
          shortNames.add(
            normalizeName(String(customer.name))
              .replace(/_/g, '-')
              .toLowerCase()
          )
      })

      return {
        existingGcns: gcns,
        existingNormalizedNames: names,
        existingShortNames: shortNames,
      }
    }, [customers])

  const minimumLength = searchMode === 'gcn' ? 1 : MIN_SEARCH_LENGTH

  const { data: nextgenSearchData, isLoading: nextgenSearchLoading } = useQuery(
    ['getNextgenCustomers', searchMode, debouncedSearch],
    async () => {
      const customers = await getNextgenCustomers(searchMode, debouncedSearch)

      return { customers: customers ?? ([] as NextgenCustomer[]) }
    },
    {
      enabled: Boolean(
        debouncedSearch && debouncedSearch.trim().length >= minimumLength
      ),
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 2,
      cacheTime: 1000 * 60 * 5,
      onError: (err) => {
        console.error('GetNextgenCustomers failed', err)
      },
    }
  )

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.trim().length < minimumLength) {
      setNextgenCustomers([])
      return
    }

    setNextgenCustomers(nextgenSearchData?.customers ?? [])
  }, [nextgenSearchData, debouncedSearch, minimumLength])

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(searchValue),
      DEBOUNCE_MS
    )
    return () => clearTimeout(timeout)
  }, [searchValue])

  const handleModeChange = useCallback(
    (mode: 'gcn' | 'name') => {
      if (mode === searchMode) return
      setSearchMode(mode)
      setSearchValue('')
      setDebouncedSearch('')
      setNextgenCustomers([])
      selectedNextgenCustomerRef.current = null
      form.setFieldsValue({ customer: undefined })
    },
    [searchMode, form]
  )

  const searchModeOptions = [
    { label: 'Customer Name', value: 'name' },
    { label: 'GCN', value: 'gcn' },
  ]

  const isExistingCustomer = useCallback(
    (customer: NextgenCustomer) =>
      isExistingNextgenCustomer(customer, {
        existingGcns,
        existingNormalizedNames,
      }),
    [existingGcns, existingNormalizedNames]
  )

  /**
   * Customers already in TCC stay in the list as disabled "Existing" options so
   * a search for one reads as "already added" rather than "not found in LASI".
   */
  const availableNextgenOptions = useMemo(() => {
    return (nextgenCustomers || []).map((customer) => {
      const isExisting = isExistingCustomer(customer)
      const mutedColor = isExisting ? colors.nggray700 : undefined

      return {
        label: (
          <Flex justify="space-between" align="start" gap={12} fullWidth>
            <Styled.Flex direction="column" gap={4}>
              <Typography color={mutedColor}>{customer.name}</Typography>
              <Typography
                variant="12-regular"
                color={mutedColor}
              >{`GCN: ${customer.gcn}`}</Typography>
            </Styled.Flex>
            {isExisting && (
              <Styled.CustomTag color="secondary">
                {EXISTING_CUSTOMER_TAG_LABEL}
              </Styled.CustomTag>
            )}
          </Flex>
        ),
        value: makeCompoundValue(customer.gcn, customer.name),
        disabled: isExisting,
      }
    })
  }, [nextgenCustomers, isExistingCustomer, colors.nggray700])

  const createCustomer = useCallback(async (payload: Partial<ICustomer>) => {
    const normalizedDisplay = normalizeName(String(payload.name ?? ''))
    const inferredStatus =
      payload.status ??
      (payload.accountGcn && String(payload.accountGcn).trim() !== ''
        ? CustomerStatus.CUSTOMER_STATUS_CUSTOMER
        : CustomerStatus.CUSTOMER_STATUS_INTERNAL)
    const resp = await createCustomerRequest({
      customer: {
        id: '',
        accountNumber: uuid.v4(),
        name: normalizedDisplay,
        shortName:
          payload.shortName ??
          normalizeName(String(payload.name ?? '')).replace(/_/g, '-'),
        status: inferredStatus,
        accountGcn: payload.accountGcn ?? '',
      },
      firstUser: undefined,
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (resp && (resp as any).customer && (resp as any).customer.id) || null
  }, [])

  /**
   * Customers may only be created from a LASI search result, so an option is
   * only accepted when it resolves back to one.
   */
  const selectNextgenCustomer = useCallback(
    (val: unknown) => {
      const { gcn, name } = parseCompoundValue(String(val))
      const match = nextgenCustomers.find(
        (customer) =>
          String(customer.gcn) === gcn && String(customer.name || '') === name
      )
      const selectable = match && !isExistingCustomer(match) ? match : null

      selectedNextgenCustomerRef.current = selectable
      form.setFieldsValue({ customer: selectable?.name ?? '' })
    },
    [nextgenCustomers, isExistingCustomer, form]
  )

  const clearSearch = useCallback(() => {
    setSearchMode('name')
    selectedNextgenCustomerRef.current = null
    setSearchValue('')
    setDebouncedSearch('')
    form.setFieldsValue({ customer: undefined })
  }, [form])

  const handleOnOpenChange = useCallback(
    (val: boolean) => {
      form.resetFields()
      clearSearch()
      modal.toggle(val)
    },
    [form, modal, clearSearch]
  )

  const onFormFinish = useCallback(async () => {
    try {
      setIsLoading(true)

      const taken = new Set<string>([
        ...Array.from(existingNormalizedNames),
        ...Array.from(existingShortNames),
      ])

      const selected = selectedNextgenCustomerRef.current
      if (!selected) throw new Error(UNSOURCED_CUSTOMER_MESSAGE)

      const mappedStatus = selected.gcn
        ? CustomerStatus.CUSTOMER_STATUS_CUSTOMER
        : String(selected.status || '').toLowerCase() === 'customer'
          ? CustomerStatus.CUSTOMER_STATUS_CUSTOMER
          : CustomerStatus.CUSTOMER_STATUS_INTERNAL

      const { display, short } = generateUniqueNamePair(
        String(selected.name ?? ''),
        taken,
        selected.gcn
      )

      const newCustomerId = await createCustomer({
        name: display,
        shortName: short,
        status: mappedStatus,
        accountGcn: selected.gcn ?? '',
      })

      existingNormalizedNames.add(normalizeName(display))
      existingShortNames.add(short.toLowerCase())

      queryClient.invalidateQueries('getCustomers')
      displayGlobalMessage({
        heading: 'Customer created successfully.',
        type: 'success',
      })

      modal.hide()
      if (newCustomerId) {
        navigate(`/customer-management/${newCustomerId}`)
      } else {
        navigate('/customer-management')
      }
    } catch (err) {
      basicErrorHandler(err)
    } finally {
      setIsLoading(false)
    }
  }, [
    basicErrorHandler,
    createCustomer,
    displayGlobalMessage,
    modal,
    navigate,
    queryClient,
    existingNormalizedNames,
    existingShortNames,
  ])

  return (
    <Modal open={modal.visible} onOpenChange={handleOnOpenChange}>
      <Modal.Trigger asChild>
        <Button
          aria-label="Add new customer"
          icon={<Add sx={{ fontSize: 12 }} />}
          disabled={isStgOrPrd() && !isAdmin}
        >
          Add New Customer
        </Button>
      </Modal.Trigger>
      <Modal.Display aria-label="Add new customer">
        <Modal.Header title="Add New Customer" />
        <Modal.Content>
          <Flex direction="column" gap={16}>
            <Typography>
              You can add a new customer by searching using the customer name or
              GCN.
            </Typography>
            <Form name={FORM_NAME} form={form} onFinish={onFormFinish}>
              <Flex gap={16} direction="column">
                <Flex gap={16}>
                  <Styled.FormItem initialValue={'name'}>
                    <Select
                      options={searchModeOptions}
                      value={searchMode}
                      onChange={(val: string) =>
                        handleModeChange(val as 'gcn' | 'name')
                      }
                      popupMatchSelectWidth={false}
                    />
                  </Styled.FormItem>
                  <FormItem
                    required
                    name="customer"
                    fullWidth
                    rules={[
                      { required: true, message: 'Customer is required.' },
                      {
                        validator: async (_, raw) => {
                          if (!raw || String(raw).trim() === '')
                            return Promise.resolve()

                          const selected = selectedNextgenCustomerRef.current
                          if (!selected) {
                            return Promise.reject(
                              new Error(UNSOURCED_CUSTOMER_MESSAGE)
                            )
                          }

                          const candidate = normalizeName(String(raw ?? ''))
                          if (!candidate)
                            return Promise.reject(
                              new Error('Customer is required.')
                            )

                          if (!PRACTICE_REGEX.test(candidate)) {
                            return Promise.reject(
                              new Error(
                                `Use lowercase letters/digits, may include - or _, must start/end with a letter or digit. ${candidate}`
                              )
                            )
                          }

                          if (
                            selected.gcn &&
                            existingGcns.has(String(selected.gcn).toLowerCase())
                          ) {
                            return Promise.reject(
                              new Error('Selected GCN is already in use.')
                            )
                          }

                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      filterOption={false}
                      options={availableNextgenOptions}
                      value={form.getFieldValue('customer')}
                      onSearch={(value) => {
                        selectedNextgenCustomerRef.current = null
                        setSearchValue(value)
                      }}
                      onSelect={selectNextgenCustomer}
                      placeholder={placeholderText}
                      prefix={
                        <Search
                          sx={{ fontSize: 20, color: colors.nggray600 }}
                        />
                      }
                      onChange={selectNextgenCustomer}
                      notFoundContent={
                        searchValue
                          ? (() => {
                              if (debouncedSearch && nextgenSearchLoading) {
                                return (
                                  <Flex align="center">
                                    <Typography>
                                      Searching for matches…
                                    </Typography>
                                  </Flex>
                                )
                              }

                              if (!debouncedSearch) {
                                return (
                                  <Typography>
                                    Type to search customer
                                  </Typography>
                                )
                              }

                              return (
                                <Typography>
                                  {UNSOURCED_CUSTOMER_MESSAGE}
                                </Typography>
                              )
                            })()
                          : null
                      }
                      suffixIcon={<></>}
                    />
                  </FormItem>
                </Flex>
              </Flex>
            </Form>
          </Flex>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Add',
              form: FORM_NAME,
              type: 'submit',
              loading: isLoading,
              disabled: isLoading,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
